import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Avatar from '@mui/material/Avatar'
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'

import useSubmitting from '../../hooks/useSubmitting'
import NoticeBox from './NoticeBox'
import { fieldErrorText, formatDeviseErrors, splitFieldErrors } from './deviseErrors'
import { deviseFetch, showFailedRequest } from './deviseRequest'
import { setFlashNotice, setFlashAlert, clearFlashAlert } from '../../redux/features/ui/uiSlice'
import { loadUserData, selectAuthToken, selectCurrentUser } from '../../redux/features/user/userSlice'

const VERIFICATION_FIELDS = ['login', 'email']

// Shown instead of the normal authenticated app for a version-1 account
// (0.6.27.16): signed in, but never upgraded to version 2. The
// server-side flow this wires up (User::VerificationController) existed
// before the React SPA did, reachable only through an old HTML page
// until now. Pre-filled from this account's own login/email -- most
// version-1 accounts already have both ("signing in works" is exactly
// why they get here rather than being blocked earlier); this is mainly
// for the ones missing one or the other, though either can be edited
// regardless.
//
// Submitting this almost always leads to confirming an email next:
// confirmation_required? only starts applying once verified? is true,
// and these accounts predate Devise's confirmable entirely, so
// confirmed_at is NULL for nearly all of them. See
// ConfirmationInstructions.jsx, which this hands off to on success.
export default function VerifyAccount() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const authToken = useSelector(selectAuthToken)
  const currentUser = useSelector(selectCurrentUser)
  const [errors, setErrors] = useState({})
  const [submitting, submit] = useSubmitting()

  const loginHint = currentUser?.login || ''
  const emailHint = currentUser?.email || currentUser?.unconfirmed_email || ''

  const handleSubmit = (evt) => {
    evt.preventDefault()
    setErrors({})
    dispatch(clearFlashAlert())

    const data = new FormData(evt.currentTarget)

    submit(async () => {
      const result = await deviseFetch(dispatch, '/user/verification.json', {
        method: 'POST',
        body: data,
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': authToken,
        },
      })

      if (result.failure) {
        showFailedRequest(dispatch, result)
        return
      }
      const { response, json: json_data } = result

      if (response.status === 201) {
        dispatch(loadUserData(json_data))
        dispatch(setFlashNotice('Account verified. Check your email to confirm it.'))
        navigate('/app/confirmation_instructions')
        return
      }

      const { fieldErrors, flashMessage } = response.status === 422
        ? splitFieldErrors(json_data, VERIFICATION_FIELDS, 'this account from being verified')
        : { fieldErrors: {}, flashMessage: formatDeviseErrors(json_data) }
      setErrors(fieldErrors)
      dispatch(setFlashAlert(flashMessage))
    })
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 9,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component='h1' variant='h5'>
          Verify your account
        </Typography>

        <NoticeBox>
          Stikis needs to verify some details about your account. Check
          your login and email below, and update them if you need to.
        </NoticeBox>

        <Box
          component="form"
          sx={{ mt: 1, width: '100%', maxWidth: 400 }}
          onSubmit={handleSubmit}
          noValidate
        >
          <TextField
            margin='normal'
            required
            fullWidth
            label='Login'
            id='user[login]'
            name='user[login]'
            autoComplete='login'
            defaultValue={loginHint}
            error={Boolean(errors.login)}
            helperText={fieldErrorText(errors, 'login')}
            autoFocus
          />
          <TextField
            margin='normal'
            required
            fullWidth
            id='user[email]'
            label='Email address'
            name='user[email]'
            type='email'
            autoComplete='email'
            defaultValue={emailHint}
            error={Boolean(errors.email)}
            helperText={fieldErrorText(errors, 'email')}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            loading={submitting}
            sx={{ mt: 3, mb: 2 }}
          >
            Verify
          </Button>
        </Box>
      </Box>
    </Container>
  )
}
